/*
 * Copyright (c) 2022 CollegeBoard
 *
 * Created on: 5/6/22, 2:09 PM
 * Created by: apudikov
 *
 * Filename:   index.tsx
 *
 * Description: Context & Hook to build a list of checkboxes with select/unselect all checkbox
 *              Provider:
 *                  <ChecksWithSelectAllProvider checkIDs={<array of unique check IDs>} [initialValues={<array of initial values>}]>
 *                      ...
 *                  </ChecksWithSelectAllProvider>
 *
 *              Usage:
 *                  const {
 *                      allChecked,   // use to set checked property of the "Select All" checkbox
 *                      setAllChecked,// use this to force trigger select/unselect all
 *                      isChecked,    // use this function to check whether a particular check with id is checked or not
 *                                    // can be set as a handler for the checked prop of a checkbox
 *                      setIsChecked, // use this to force check - set as the onClick/onCheck event handler
 *                      anyChecked,   // flag if anything is checked
 *                      getAllChecked,// use this to get IDs of all the checked checkboxes
 *                  } = useChecksWithSelectAll();
 *
 *              Single Checkbox:
 *                  const { isChecked, setIsChecked } = useChecksWithSelectAll();
 *                  const rowID = <unique row ID>;
 *                  return <Checkbox checked={isChecked(rowID)} onChange={checked => setIsChecked!(rowID, checked)} ...>
 *
 *              Check All Checkbox
 *                  const { allChecked, setAllChecked } = useChecksWithSelectAll();
 *                  return <Checkbox checked={allChecked} onChange={setAllChecked} ... />;
 *
 *              Print Button (that gets enabled if anything is checked)
 *                  const { anyChecked, getAllChecked } = useChecksWithSelectAll();
 *                  return <Button disabled={!anyChecked} onClick={() => { printIDs(getAllChecked()) }} />
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { pick } from 'lodash';
const ChecksWithSelectAllContext = React.createContext({
    allChecked: false,
    isChecked: () => false,
    anyChecked: false,
    getAllChecked: () => [],
    setCheckAllScope: () => { },
    selectedCount: 0,
});
export const ChecksWithSelectAllProvider = ({ checkIDs, initialValues, checkAllScope: allScope, children, }) => {
    const [anyChecked, setAnyChecked] = useState(false);
    const [allChecked, setAllChecked] = useState(false);
    const [checkedMap, setCheckedMap] = useState({});
    const [checkAllScope, setCheckAllScope] = useState(allScope);
    // Update scope if the input param changes
    useEffect(() => {
        setCheckAllScope(allScope);
    }, [allScope]);
    /** Check/uncheck a single checkbox */
    const setIsChecked = (id, checked) => {
        setCheckedMap(Object.assign(Object.assign({}, checkedMap), { [id]: checked }));
    };
    /** Check if a single checkbox is checked */
    const isChecked = (id) => {
        return checkedMap[id] || false;
    };
    /** if checkIDs input param changed - regenerate the checkedMap */
    useEffect(() => {
        const initialIsChecked = checkIDs.reduce((acc, d, index) => {
            acc[d] = initialValues ? initialValues[index] : false;
            return acc;
        }, {});
        setCheckedMap(initialIsChecked);
    }, [checkIDs]);
    /** Force Check All - called by the "All" check and exposed as setAllChecked */
    const forceCheckAll = (checked) => {
        setAllChecked(checked);
        setCheckedMap(current => {
            const nextIsChecked = {};
            Object.keys(current).forEach(key => {
                if (!checkAllScope || checkAllScope.includes(key)) {
                    nextIsChecked[key] = checked;
                }
                else {
                    nextIsChecked[key] = current[key];
                }
            });
            return nextIsChecked;
        });
    };
    /** Validate the checkedMap - set allChecked correspondingly if all checked */
    useEffect(() => {
        const scopeChecks = checkAllScope ? pick(checkedMap, checkAllScope) : checkedMap;
        // All checked - scope applied if available
        setAllChecked(Object.values(scopeChecks).every(el => el));
        // Any checked - no scope applied
        setAnyChecked(Object.values(checkedMap).some(el => el));
    }, [checkedMap, checkAllScope]);
    /** Retrieve IDs of all checked items */
    const getAllChecked = () => {
        // get all the entries, get all the true values and return the keys
        return Object.entries(checkedMap)
            .filter(([, value]) => value)
            .map(([key]) => key);
    };
    /** Retrieve the selected items count  */
    const countAllSelected = () => {
        return Object.entries(checkedMap).filter(([, value]) => value).length;
    };
    /** Context Value */
    const context = useMemo(() => ({
        allChecked,
        setAllChecked: forceCheckAll,
        isChecked,
        setIsChecked,
        anyChecked,
        getAllChecked,
        setCheckAllScope,
        selectedCount: countAllSelected(),
    }), [allChecked, isChecked, anyChecked, getAllChecked, setCheckAllScope]);
    return React.createElement(ChecksWithSelectAllContext.Provider, { value: context }, children);
};
/** Context Hook */
export const useChecksWithSelectAll = () => useContext(ChecksWithSelectAllContext);
//# sourceMappingURL=index.js.map